import * as React from 'react';

import { IcClose } from '@/assets/images/svgs';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetTitle,
} from '@/components/TailwindComponents/base';
import { cn } from '@/components/TailwindComponents/lib/utils';

export interface SidePaneModalProps {
  title: string;
  children?: React.ReactNode;
  headerContent?: React.ReactNode;
  side?: 'left' | 'right' | 'top' | 'bottom';
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  overlayClass?: string;
  className?: string;
  headerClassName?: string;
  footerClassName?: string;
  disableClickAway?: boolean;
  /**
   * Radix Dialog (Sheet) modal mode.
   * When true (default), pointer-events are disabled outside the sheet.
   * Set false when you need to interact with another portal-based modal/dialog on top (e.g. MUI Dialog).
   */
  modal?: boolean;
}

const SidePaneModal: React.FC<SidePaneModalProps> & {
  Content: React.FC<{ children: React.ReactNode; className?: string }>;
  Footer: React.FC<{ children: React.ReactNode; className?: string }>;
} = ({
  title,
  headerContent,
  children,
  side = 'right',
  open = false,
  modal = true,
  onOpenChange,
  overlayClass,
  className,
  headerClassName,
  footerClassName,
  disableClickAway = false,
}: SidePaneModalProps) => {
  let content: React.ReactNode = null;
  let footer: React.ReactNode = null;
  const extras: React.ReactNode[] = [];

  const handleOpenChange = (isOpen: boolean) => {
    onOpenChange?.(isOpen);
  };

  const findComponents = (nestedChildren: React.ReactNode) => {
    React.Children.forEach(nestedChildren, (child) => {
      if (React.isValidElement(child)) {
        if (child.type === Footer) {
          footer = child;
        } else if (child.type === Content) {
          content = child;
        } else if (child.props?.children) {
          // Recurse into wrapper components (e.g. fragments/divs) to find Content/Footer nested within.
          findComponents(child.props.children);
        } else {
          // Render any other direct children inside the sheet as-is (e.g. portal-less dialogs).
          extras.push(child);
        }
      }
    });
  };

  findComponents(children);

  const footerWithClassName =
    footer && footerClassName
      ? React.cloneElement(footer as React.ReactElement, {
          className: cn(
            (footer as React.ReactElement).props?.className,
            footerClassName,
          ),
        })
      : footer;

  const hasCustomWidth =
    className?.includes('w-[') || className?.includes('w-');

  return (
    <Sheet open={open} onOpenChange={handleOpenChange} modal={modal}>
      {/* &>button]:hidden is a hack to hide the X default button */}
      <SheetContent
        side={side}
        className={cn(
          'flex flex-col h-full [&>button]:hidden z-[2251]',
          hasCustomWidth
            ? cn(className, 'max-w-none sm:max-w-none')
            : className || 'w-[400px] sm:w-[540px]',
        )}
        onOpenAutoFocus={(e) => e.preventDefault()}
        overlayClass={cn('z-[1300]', overlayClass)}
        onInteractOutside={
          disableClickAway
            ? (e) => {
                // Prevent click-away from dismissing the sheet.
                // Note: when `modal={false}`, this still allows interacting with other portal-based dialogs,
                // but stops Radix from auto-closing the sheet.
                e.preventDefault();
              }
            : undefined
        }
        onEscapeKeyDown={
          disableClickAway
            ? (e) => {
                // Keep the sheet open on Escape when click-away is disabled.
                e.preventDefault();
              }
            : undefined
        }
      >
        <div
          className={cn(
            '-mx-6 -mt-6 px-6 py-4 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between text-gray-900 dark:text-gray-100',
            headerClassName,
          )}
        >
          <SheetTitle className="text-lg font-semibold">{title}</SheetTitle>
          <div className="flex items-center h-full">
            {headerContent}
            <SheetClose asChild>
              <div className="flex items-center justify-center w-9 h-9 cursor-pointer rounded-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300">
                <IcClose className="h-5 w-5" />
              </div>
            </SheetClose>
          </div>
        </div>

        {content || null}
        {footerWithClassName || null}
        {extras.length > 0 ? extras : null}
      </SheetContent>
    </Sheet>
  );
};

interface ContentProps {
  children: React.ReactNode;
  className?: string;
}

const Content: React.FC<ContentProps> = ({ children, className }) => {
  return (
    <div className={cn('flex-1 overflow-y-auto py-6', className)}>
      {children}
    </div>
  );
};

interface FooterProps {
  children: React.ReactNode;
  className?: string;
}

const Footer: React.FC<FooterProps> = ({ children, className }) => {
  return (
    <SheetFooter
      className={cn(
        'mt-auto -mx-6 -mb-6 px-6 py-4 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-2 text-gray-900 dark:text-gray-100',
        className,
      )}
    >
      {children}
    </SheetFooter>
  );
};

SidePaneModal.Content = Content;
SidePaneModal.Content.displayName = 'SidePaneModal.Content';

SidePaneModal.Footer = Footer;
SidePaneModal.Footer.displayName = 'SidePaneModal.Footer';

export { SidePaneModal };
